import { useEffect, useRef, useState } from 'react';
import type { FC } from 'react';

import { Placeholder, useCms, useContent, usePlaceholder, useRendering } from '@eneco/cms';
import logger from '@eneco/common/log';
import { toURL } from '@eneco/common/path';
import { stripAccolades } from '@eneco/common/string';
import { Recaptcha } from '@eneco/components/Recaptcha/Recaptcha';
import type { RecaptchaRef } from '@eneco/components/Recaptcha/Recaptcha';
import { RecaptchaForm } from '@eneco/components/RecaptchaForm/RecaptchaForm';
import RichText from '@eneco/components/RichText/RichText';
import { useFormsPublicV2PostFormV2 } from '@eneco/dc/hooks';
import useDC from '@eneco/dc/useDC';
import { typedYupResolver } from '@eneco/hooks/form';
import { useRouter } from '@eneco/next';
import { Button, Heading, NotificationBox, Stack } from '@eneco/sparky';
import { FormProvider, useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';

import { formFieldComponents } from './generic-form/fields';
import { prepareRequest } from './generic-form/request';
import { buildFormSchema } from './generic-form/schema';
import type { FormValues } from './generic-form/types';
import type { GenericFormContent } from './generic-form-content.types';
import { GENERIC_FORM_SLOTS, resolveSlot } from './slots';

/**
 * `GenericForm` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Unlike every other block in this registry it is not presentational: it submits to the Digital
 * Core, which is why `lib:cms-components` may now depend on `lib:dc`/`lib:hooks` and why
 * `CmsRouteContainer` mounts a `DigitalCoreProvider` (guest-only in this lane).
 *
 * Validation is assembled from the rendered children (`buildFormSchema`), so a field type this lane
 * has not migrated yet leaves the rest of the form working rather than blocking submission.
 */
const GenericForm: FC = () => {
  const { adapter } = useCms();
  const content = useContent<GenericFormContent>();
  const rendering = useRendering();
  const { label, businessUnit } = useDC();
  const { pushHref } = useRouter();
  const { error, isError, isSuccess, send } = useFormsPublicV2PostFormV2();

  const recaptchaRef = useRef<RecaptchaRef>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const mainSlot = resolveSlot(rendering, GENERIC_FORM_SLOTS.main);
  const fields = usePlaceholder(mainSlot ?? GENERIC_FORM_SLOTS.main[0]);

  const methods = useForm<FormValues>({
    mode: 'onBlur',
    resolver: typedYupResolver<FormValues>(buildFormSchema(fields, formFieldComponents)),
  });
  const {
    formState: { errors },
  } = methods;

  const redirect = adapter.toLink(content.formSubmitRedirectLink);

  useEffect(() => {
    if (isError) logger.error('Wxd7AY', 'Failed to submit form', { error });
  }, [isError, error]);

  useEffect(() => {
    if (!isSuccess || !redirect?.href) return;
    pushHref(toURL(redirect.href));
  }, [isSuccess, redirect?.href, pushHref]);

  const submitForm: SubmitHandler<FormValues> = async values => {
    setIsSubmitting(true);
    try {
      await send({
        businessUnit,
        formId: stripAccolades(content.senderIdOption ?? ''),
        label,
        requestBody: prepareRequest(content.senderIdOption ?? '', values),
        recaptchaEnterpriseToken: values.recaptchaToken,
      } as never);
    } catch (submitError) {
      logger.error('Wxd7AY', 'Failed to submit form', { error: submitError });
    } finally {
      setIsSubmitting(false);
    }
  };

  const description = adapter.toRichText(content.description);

  return (
    <FormProvider {...methods}>
      <RecaptchaForm onSubmit={submitForm} recaptchaRef={recaptchaRef}>
        <Stack gap="6">
          {content.title && (
            <Heading as="h2" size="M">
              {content.title}
            </Heading>
          )}
          {description && <RichText html={description} />}
          {mainSlot ? <Placeholder name={mainSlot} /> : null}
          <Stack.Item>
            <Recaptcha hasError={Boolean(errors.recaptchaToken)} ref={recaptchaRef} />
          </Stack.Item>
          <Stack.Item>
            <Button type="submit" isLoading={isSubmitting}>
              {redirect?.text ?? 'Verstuur'}
            </Button>
          </Stack.Item>
        </Stack>
      </RecaptchaForm>
      {isError && <NotificationBox isAlert title="Er ging iets mis" text="Probeer het later opnieuw." />}
    </FormProvider>
  );
};

export default GenericForm;
